import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Font Awesome Icon Picker Component
 * Displays a searchable grid of Font Awesome icons
 */

// Popular icons to show first
const POPULAR_ICONS = [
  'faUser', 'faUsers', 'faBuilding', 'faServer', 'faDesktop', 'faLaptop', 'faMobile',
  'faEnvelope', 'faPhone', 'faGlobe', 'faCloud', 'faDatabase', 'faNetworkWired',
  'faShieldAlt', 'faLock', 'faKey', 'faCheckCircle', 'faTimesCircle', 'faCog',
  'faTools', 'faWrench', 'faChartLine', 'faChartBar', 'faFileAlt', 'faFolder',
  'faDownload', 'faUpload', 'faSync', 'faPlus', 'faMinus', 'faEdit', 'faTrash',
  'faSave', 'faPrint', 'faSearch', 'faFilter', 'faBell', 'faCalendar', 'faClock',
  'faMapMarkerAlt', 'faHome', 'faIndustry', 'faStore', 'faWarehouse', 'faTruck'
];

export default function IconPicker({ value, onChange, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isDarkMode } = useTheme();

  // Get all solid icons
  const allIcons = useMemo(() => {
    const icons = [];
    for (const [key, icon] of Object.entries(solidIcons)) {
      if (key.startsWith('fa') && typeof icon === 'object' && icon.iconName) {
        icons.push({ key, icon, name: icon.iconName });
      }
    }
    return icons;
  }, []);

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    if (!searchTerm) {
      // Show popular icons first when no search
      const popular = allIcons.filter(i => POPULAR_ICONS.includes(i.key));
      const others = allIcons.filter(i => !POPULAR_ICONS.includes(i.key));
      return [...popular, ...others];
    }
    
    const term = searchTerm.toLowerCase();
    return allIcons.filter(i => 
      i.name.toLowerCase().includes(term) || 
      i.key.toLowerCase().includes(term)
    );
  }, [allIcons, searchTerm]);

  const handleIconSelect = (iconKey) => {
    onChange(iconKey);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Get the current icon object
  const currentIcon = value && solidIcons[value] ? solidIcons[value] : null;

  return (
    <div className={`relative ${className}`}>
      {/* Display button with selected icon */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all flex items-center justify-between group"
        >
          <span className="flex items-center gap-3">
            {currentIcon ? (
              <>
                <FontAwesomeIcon icon={currentIcon} className="text-xl text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{currentIcon.iconName}</span>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400 text-sm">Click to select an icon</span>
            )}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-all ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {/* Clear button */}
        {currentIcon && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="px-3 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-500 hover:text-red-600 dark:hover:text-red-400 transition-all"
            title="Clear icon"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Icon picker modal overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Picker panel - centered on screen */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90vw] max-w-[800px] max-h-[85vh] bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header with search and close button */}
            <div className="flex-shrink-0 p-5 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-900">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Choose an Icon</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search icons... (try: user, home, chart, gear, etc.)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Icons grid - scrollable */}
            <div className="flex-1 overflow-y-auto p-5 bg-gray-50 dark:bg-gray-900">
              {filteredIcons.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No icons found</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try a different search term</p>
                </div>
              ) : (
                <>
                  {!searchTerm && (
                    <div className="mb-4">
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Popular Icons
                      </h4>
                    </div>
                  )}
                  <div className="grid grid-cols-10 sm:grid-cols-12 lg:grid-cols-14 xl:grid-cols-16 gap-2">
                    {filteredIcons.slice(0, searchTerm ? undefined : POPULAR_ICONS.length).map(({ key, icon }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleIconSelect(key)}
                        className={`aspect-square p-3 rounded-lg transition-all flex items-center justify-center group relative ${
                          value === key
                            ? 'bg-blue-600 text-white shadow-lg scale-105 ring-2 ring-blue-400'
                            : 'bg-white dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-110 shadow-sm hover:shadow-md'
                        }`}
                        title={icon.iconName}
                      >
                        <FontAwesomeIcon icon={icon} className="text-xl" />
                        {value === key && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                    {!searchTerm && filteredIcons.length > POPULAR_ICONS.length && (
                      <>
                        <div className="col-span-full my-4">
                          <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            All Icons
                          </h4>
                        </div>
                        {filteredIcons.slice(POPULAR_ICONS.length).map(({ key, icon }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => handleIconSelect(key)}
                            className={`aspect-square p-3 rounded-lg transition-all flex items-center justify-center group relative ${
                              value === key
                                ? 'bg-blue-600 text-white shadow-lg scale-105 ring-2 ring-blue-400'
                                : 'bg-white dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:scale-110 shadow-sm hover:shadow-md'
                            }`}
                            title={icon.iconName}
                          >
                            <FontAwesomeIcon icon={icon} className="text-xl" />
                            {value === key && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer info */}
            <div className="flex-shrink-0 px-5 py-4 bg-white dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-400 font-medium">
                    {filteredIcons.length} icon{filteredIcons.length !== 1 ? 's' : ''} {searchTerm ? 'found' : 'available'}
                  </span>
                </div>
                {currentIcon && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FontAwesomeIcon icon={currentIcon} className="text-blue-600 dark:text-blue-400" />
                    <span className="text-blue-700 dark:text-blue-300 font-semibold text-sm">
                      {currentIcon.iconName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

